import { createContext, ReactNode, useState } from "react";
import { DARK_THEME, LIGHT_THEME } from "../styles/const.theme.ts";
import { safeLocalStorage } from "../utils/safeLocalStroage.ts";

// Define the types for the context value
interface ThemeContextType {
  theme: string;
  changeTheme: (theme: string) => void;
}

function initTheme() {
  const localTheme = safeLocalStorage.getItem("theme");
  if (localTheme) return JSON.parse(localTheme);
  if (typeof window === "undefined") return;
  if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return DARK_THEME;
  }
  return LIGHT_THEME;
}

// Create the context with default values
export const ThemeContext = createContext<ThemeContextType>({
  theme: initTheme(),
  changeTheme: () => {
    console.log("changeTheme before init");
  },
});

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<string>(() => {
    const theme = initTheme();
    document.documentElement.setAttribute("data-theme", theme);
    return theme;
  });

  const changeTheme = (theme: string) => {
    setTheme(theme);
    document.documentElement.setAttribute("data-theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');

    const color = getComputedStyle(document.documentElement)
      .getPropertyValue("--pf")
      .trim();

    if (color) {
      meta!.setAttribute("content", color);
    }

    safeLocalStorage.setItem("theme", JSON.stringify(theme));
  };

  return (
    <ThemeContext.Provider
      value={{
        theme: theme,
        changeTheme: changeTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
